import constants from '../../constants/constants';

import { getEntity } from '../entity/entity-service';

import { secondsToMinutes } from '../time/time-service';

import { getGameSession } from '../../modules/game_session';
import { getGameSessionActivitiesAsync } from '../../modules/game_session_api';
import { createPlayerGameSession } from '../../modules/player_game_session';
import {
  createPlayerGameSessionAsync,
  getPlayerLastPlayedAssignedSession,
} from '../../modules/player_game_session_api';
import { createPlayerGameSessionActivity } from '../../modules/player_game_session_activity';
import { getGameSessionActivities } from '../../modules/game_session_activity';

import { getDifficulties } from '../../modules/system/difficulty';
import { getGames } from '../../modules/system/game';
import { getPositions } from '../../modules/system/position';
import { getLevels } from '../../modules/system/level';
import { getSkills } from '../../modules/system/skill';
import { buildActivityCard, buildActivityCardWithData } from '../activity/activity-service';

export const calculateDurations = (games, activities) => {
  const gameActivityDurations =
    games &&
    games.length > 0 &&
    activities.map((activity) => {
      const game = games.find((_game) => _game.id === activity.game);
      return game ? game.duration : 0;
    });

  const durations =
    gameActivityDurations &&
    gameActivityDurations.length > 0 &&
    gameActivityDurations.reduce((total, current) => total + current);
  return secondsToMinutes(durations || 0);
};

export const buildSingleGameSessionURL = (userToken) => {
  const port = window.location.port;

  let platformURL = 'https://' + window.location.hostname;

  if (port !== '') {
    platformURL += ':' + port;
  }

  const nextURL = new URL(
    constants.PLAY_CONTENT_URL.startsWith('http') ? constants.PLAY_CONTENT_URL : `https:${constants.PLAY_CONTENT_URL}`
  );
  nextURL.pathname = '/play_game';
  nextURL.searchParams.append('single_game_id', 'ALL');
  nextURL.searchParams.append('auth', userToken);
  nextURL.searchParams.append('purl', platformURL); // do not URL encode
  return nextURL.href;
};

export const buildOutOfGymsessionURL = (playerID, userToken) => {
  const port = window.location.port;

  let platformURL = 'https://' + window.location.hostname;

  if (port !== '') {
    platformURL += ':' + port;
  }

  platformURL = encodeURIComponent(platformURL);

  return (
    constants.PLAY_CONTENT_URL + `/play_game?out_of_gym_session_id=${playerID}&auth=${userToken}&purl=${platformURL}`
  );
};

export const buildPlayGameUrl = (gameSessionId, userToken) => {
  const port = window.location.port;

  let platformURL = 'https://' + window.location.hostname;

  if (port !== '') {
    platformURL += ':' + port;
  }

  platformURL = encodeURIComponent(platformURL);
  return (
    constants.PLAY_CONTENT_URL + `/play_game?game_session_id=${gameSessionId}&auth=${userToken}&purl=${platformURL}`
  );
};

export function buildGameSessionWithData(gameSession) {
  const activities = gameSession.activities;
  const activityCards = activities.map((activity) => buildActivityCardWithData(activity));

  gameSession._activityCards = activityCards;
  gameSession._activities = activities;
  return gameSession;
}

export async function buildGameSession(dispatch, gameSession, gameSessionId, gameOptions, userToken) {
  if (typeof userToken === 'undefined') {
    userToken = false;
  }

  let _gameSession = gameSession;

  if (!_gameSession) {
    _gameSession = await getEntity(dispatch, getGameSession(gameSessionId));
  }

  const activitiesResponse = await getGameSessionActivitiesAsync(gameSessionId, userToken);
  let activities = [];
  if (activitiesResponse && activitiesResponse.data && activitiesResponse.data.success) {
    activities = activitiesResponse.data.activities;
  }

  const activityCards = activities.map((activity) => buildActivityCard(activity, gameOptions, true));

  _gameSession._activities = activities;
  _gameSession._activityCards = activityCards;

  return _gameSession;
}

// needs to be bound to class
export async function playActiveSession(dispatch, player, userId, playerGameSession, gameSession, isFindActive) {
  let _playerGameSession = playerGameSession;
  if ((!_playerGameSession && isFindActive) || gameSession) {
    let activeGameSessionId;
    if (typeof player.active_game_session === 'object') {
      activeGameSessionId = player.active_game_session.id;
    } else {
      activeGameSessionId = player.active_game_session;
    }

    if (!isFindActive && gameSession) {
      activeGameSessionId = gameSession.id;
    }

    if (!gameSession && !activeGameSessionId) {
      const lastPlayedGameSession = await getPlayerLastPlayedAssignedSession(player.id);

      if (lastPlayedGameSession.data.last_played_game_session_id !== -1) {
        activeGameSessionId = lastPlayedGameSession.data.last_played_game_session_id;
      }
    }

    if (activeGameSessionId) {
      return createPlayerGameSessionAsync(player.id, activeGameSessionId);
    }

    let activeGameSession;
    if (!activeGameSession) {
      activeGameSession = await getEntity(dispatch, getGameSession(activeGameSessionId));
    }

    const activities = await getEntity(
      dispatch,
      getGameSessionActivities([{ id: `[${activeGameSession.game_session_activities}]` }]),
      true
    );
    let activePlayerGameSession = {
      name: activeGameSession.name,
      game_session: activeGameSession.id,
      player: player.id,
      user: userId,
      is_active: true,
    };

    activePlayerGameSession = await getEntity(dispatch, createPlayerGameSession(activePlayerGameSession));
    _playerGameSession = activePlayerGameSession;

    for (const activity of activities) {
      const playerGameSessionActivity = {
        index: activity.index,
        game: activity.game,
        game_session: activity.game_session,
        position: activity.position,
        skill: activity.skill,
        difficulty: activity.difficulty,
        level: activity.level,
        player: player.id,
        player_game_session: activePlayerGameSession.id,
      };

      await getEntity(dispatch, createPlayerGameSessionActivity(playerGameSessionActivity));
    }
  }
  return _playerGameSession.id;
}

export async function buildGameOptions(props) {
  const { dispatch } = props;

  const skills = await getEntity(dispatch, getSkills(), props, true);
  const positions = await getEntity(dispatch, getPositions(), props, true);
  const difficulties = await getEntity(dispatch, getDifficulties(), props, true);
  const levels = await getEntity(dispatch, getLevels(), props, true);
  const games = await getEntity(dispatch, getGames([]), props, true);
  return { skills, positions, difficulties, levels, games };
}
